'use client';

import type { MultiSelectProps } from '@magicschool/spellbook/components/MultiSelect';
import type { AutocompleteBaseContextState, AutocompleteOptionType } from '@magicschool/spellbook/lib/autocomplete';
import { getOptionByValue as getOptionByValueUtil, updateLabelCache, useAutocomplete } from '@magicschool/spellbook/lib/autocomplete';
import type { FC, KeyboardEvent, MouseEvent, RefObject } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type MultiSelectOptionType = AutocompleteOptionType;

export type MultiSelectContextType = AutocompleteBaseContextState<MultiSelectOptionType> &
  Pick<
    MultiSelectProps,
    | 'disableDefaultFiltering'
    | 'descriptionOrientation'
    | 'disableTextHighlighting'
    | 'isLoading'
    | 'onBlur'
    | 'onCloseAutoFocus'
    | 'onEscapeKeyDown'
    | 'onFocus'
    | 'onInputChange'
    | 'onOpenChange'
    | 'onPointerDownOutside'
    | 'onValueChange'
    | 'disableDefaultNoResults'
    | 'text'
  > & {
    valueArray: string[];
    handleRemoveValue: (valueToRemove: string) => void;
    handleRemoveAllValues: () => void;
  };

export type MultiSelectProviderProps = Pick<
  MultiSelectProps,
  | 'children'
  | 'disableDefaultFiltering'
  | 'defaultValue'
  | 'descriptionOrientation'
  | 'disabled'
  | 'disableTextHighlighting'
  | 'isLoading'
  | 'onBlur'
  | 'onCloseAutoFocus'
  | 'onEscapeKeyDown'
  | 'onFocus'
  | 'onInputChange'
  | 'onOpenChange'
  | 'onPointerDownOutside'
  | 'onValueChange'
  | 'readOnly'
  | 'disableDefaultNoResults'
  | 'text'
  | 'value'
> & {
  initialOptions?: Map<string, { label: string; value: string }>;
  inputRef: RefObject<HTMLInputElement | null>;
};

const MultiSelectContext = createContext<MultiSelectContextType>({
  clearSearchStringIfNoValueMatches: () => undefined,
  disableDefaultFiltering: undefined,
  disableDefaultNoResults: undefined,
  descriptionOrientation: 'horizontal',
  getOptionByValue: () => undefined,
  handleInputOnBlur: () => undefined,
  handleInputOnChange: () => undefined,
  handleInputOnClick: () => undefined,
  handleInputOnFocus: () => undefined,
  handleInputOnKeyDown: () => undefined,
  handleInputOnClear: () => undefined,
  handleInputSubmit: () => undefined,
  handleOpenChange: () => undefined,
  handleOptionOnClick: () => undefined,
  handleOptionOnKeyDown: () => undefined,
  handleOptionOnMouseEnter: () => undefined,
  handleRemoveAllValues: () => undefined,
  handleRemoveValue: () => undefined,
  handleTriggerOnBlur: () => undefined,
  handleTriggerOnClick: () => undefined,
  handleTriggerOnFocus: () => undefined,
  disableTextHighlighting: true,
  inputRef: { current: null },
  isControlled: false,
  isLoading: false,
  text: { noResults: 'No results found' },
  open: false,
  optionRefs: [],
  options: new Map(),
  registerOption: () => undefined,
  searchString: '',
  setSearchString: () => undefined,
  unregisterOption: () => undefined,
  valueArray: [],
});

/**
 * Shares MultiSelect state with its sub-components.
 *
 * @headless Context provider for MultiSelect; renders only its children.
 */
export const MultiSelectProvider: FC<MultiSelectProviderProps> = ({
  children,
  disableDefaultFiltering,
  defaultValue,
  descriptionOrientation,
  disabled,
  disableDefaultNoResults,
  disableTextHighlighting,
  initialOptions,
  inputRef,
  isLoading,
  text,
  onBlur,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onFocus,
  onInputChange,
  onOpenChange,
  onPointerDownOutside,
  onValueChange,
  readOnly,
  value,
}) => {
  const type = 'select';
  const optionLabelCacheRef = useRef<Record<string, string>>({});

  const initialOptionsMap = useMemo(() => {
    if (initialOptions?.size) {
      const initialMap = new Map<string, MultiSelectOptionType>();
      initialOptions.forEach((option, key) => {
        initialMap.set(key, {
          value: option.value,
          label: option.label,
          disabled: false,
          highlightedLabel: option.label,
        });

        optionLabelCacheRef.current[key] = option.label;
      });
      return initialMap;
    }
    return new Map<string, MultiSelectOptionType>();
  }, [initialOptions]);

  const [internalValue, setInternalValue] = useState<string[]>(defaultValue || []);

  const isControlled = value !== undefined;
  const valueArray = isControlled ? value! : internalValue;
  const isEnabled = !disabled && !readOnly;

  const {
    clearHighlightedOption,
    clearSearchStringIfNoValueMatches,
    handleInputOnBlur,
    handleInputOnChange,
    handleInputOnClick,
    handleInputOnFocus,
    handleInputOnKeyDown,
    handleOpenChange,
    handleOptionOnClick: handleOptionOnClickUtil,
    handleOptionOnKeyDownArrowDown,
    handleOptionOnKeyDownArrowUp,
    handleOptionOnKeyDownEnter,
    handleOptionOnKeyDownEscape,
    handleOptionOnMouseEnter,
    handleSearchStringChange,
    handleTriggerOnBlur,
    handleTriggerOnClick,
    handleTriggerOnFocus,
    open,
    optionLabelCache,
    optionRefs,
    options,
    registerOption,
    searchString,
    setOptions,
    setSearchString,
    unregisterOption,
  } = useAutocomplete<MultiSelectOptionType>({ closeOnSelect: false, isEnabled, inputRef, onBlur, onFocus, onInputChange, onOpenChange });

  const getOptionByValue = useCallback(
    (value: string) => {
      return getOptionByValueUtil(value, options, optionLabelCache);
    },
    [options, optionLabelCache],
  );

  const handleValueChange = useCallback(
    (newValue: string[]) => {
      if (isEnabled) {
        if (!isControlled) {
          setInternalValue(newValue);
        }
        onValueChange?.(newValue);
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    },
    [isEnabled, isControlled, onValueChange, inputRef],
  );

  const handleAddValue = useCallback(
    (newValue: string) => {
      if (isEnabled) {
        if (!valueArray.includes(newValue)) {
          const newValues = [...valueArray, newValue];
          handleValueChange(newValues);
        } else if (valueArray.includes(newValue)) {
          const newValues = valueArray.filter((value) => value !== newValue);
          handleValueChange(newValues);
        }

        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    },
    [isEnabled, valueArray, handleValueChange, inputRef],
  );

  const handleRemoveValue = useCallback(
    (valueToRemove: string) => {
      if (isEnabled) {
        const newValues = valueArray.filter((value) => value !== valueToRemove);
        handleValueChange(newValues);

        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    },
    [isEnabled, valueArray, handleValueChange, inputRef],
  );

  const handleRemoveAllValues = useCallback(() => {
    if (isEnabled) {
      handleValueChange([]);

      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [isEnabled, handleValueChange, inputRef]);

  const handleOptionOnClick = useCallback(
    (event: MouseEvent<HTMLDivElement>, value: string) => {
      if (isEnabled) {
        const entry = options.get(value);

        if (entry && !entry.disabled) {
          handleOptionOnClickUtil(event, value);
          handleAddValue(entry.value!);
        }
      }
    },
    [isEnabled, options, handleAddValue, handleSearchStringChange, handleOpenChange],
  );

  const handleOptionOnKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, index: number) => {
      if (isEnabled) {
        const optionValues = Array.from(options.values());
        const entry = optionValues[index];

        if (!entry || entry.disabled) {
          return;
        }

        switch (event.key) {
          case 'Enter':
          case ' ': {
            handleOptionOnKeyDownEnter(event);
            handleAddValue(entry.value!);
            break;
          }
          case 'Tab': {
            event.preventDefault();
            handleOpenChange(false);
            clearHighlightedOption?.();
            if (inputRef.current) {
              inputRef.current.focus();
            }
            break;
          }
          case 'ArrowDown': {
            handleOptionOnKeyDownArrowDown(event, optionRefs, index);
            break;
          }
          case 'ArrowUp': {
            handleOptionOnKeyDownArrowUp(event, optionRefs, index);
            break;
          }
          case 'Escape':
          case 'Esc': {
            handleOptionOnKeyDownEscape(event);
            break;
          }
        }
      }
    },
    [isEnabled, options, handleAddValue, handleSearchStringChange, handleOpenChange, optionRefs, inputRef, clearHighlightedOption],
  );

  useEffect(() => {
    if (initialOptionsMap?.size) {
      setOptions(initialOptionsMap);
    }
  }, [initialOptionsMap, setOptions]);

  useEffect(() => {
    if (defaultValue && defaultValue.length > 0) {
      defaultValue.forEach((value) => {
        if (!options.has(value)) {
          let label = value;

          if (initialOptions?.has(value)) {
            label = initialOptions.get(value)!.label;
          } else if (optionLabelCache.current[value]) {
            label = optionLabelCache.current[value];
          } else {
            const optionElements = Array.from(document.querySelectorAll(`[data-value="${value}"]`));
            if (optionElements.length > 0 && optionElements[0] && optionElements[0].textContent) {
              label = optionElements[0].textContent;
            }
          }

          optionLabelCache.current[value] = label;

          registerOption({
            value,
            label,
            disabled: false,
            highlightedLabel: label,
          });
        }
      });
    }
  }, [defaultValue, options, registerOption, optionLabelCache, initialOptions]);

  useEffect(() => {
    updateLabelCache(options, optionLabelCache, valueArray);
  }, [options, valueArray, optionLabelCache]);

  const contextProps = {
    disableDefaultFiltering,
    defaultValue,
    descriptionOrientation,
    disableTextHighlighting,
    isLoading,
    text,
    onBlur,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onFocus,
    onInputChange,
    onOpenChange,
    onPointerDownOutside,
    onValueChange,
    type,
    disableDefaultNoResults,
    value,
  };

  const contextState = {
    isControlled,
    open,
    options,
    searchString,
    valueArray,
  };

  const contextHandlers = {
    clearSearchStringIfNoValueMatches,
    getOptionByValue,
    handleInputOnBlur,
    handleInputOnChange,
    handleInputOnClick,
    handleInputOnFocus,
    handleInputOnKeyDown,
    handleInputOnClear: () => undefined,
    handleInputSubmit: () => undefined,
    handleOpenChange,
    handleOptionOnClick,
    handleOptionOnKeyDown,
    handleOptionOnMouseEnter,
    handleRemoveAllValues,
    handleRemoveValue,
    handleTriggerOnBlur,
    handleTriggerOnClick,
    handleTriggerOnFocus,
    registerOption,
    setSearchString,
    unregisterOption,
  };

  const contextValue = useMemo<MultiSelectContextType>(
    () => ({
      ...contextProps,
      ...contextState,
      ...contextHandlers,
      inputRef,
      optionRefs,
    }),
    [contextProps, contextState, contextHandlers, inputRef, optionRefs],
  );

  return <MultiSelectContext.Provider value={contextValue}>{children}</MultiSelectContext.Provider>;
};

export const useMultiSelect = () => useContext(MultiSelectContext);

export default MultiSelectProvider;
